import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ForbiddenState } from '@web/ui/StatusPages';
import { PageLoader } from '@web/ui/PageLoader';
import { useAuth, usePermission } from './AuthProvider';
import type { PermissionRequirement } from './permissions';

interface CanProps {
  /** permission code(s); arrays are OR-ed unless `mode="all"` */
  perm: PermissionRequirement;
  mode?: 'any' | 'all';
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Renders children only when the user holds the permission.
 * @example <Can perm="iam.user.create"><Button>Invite</Button></Can>
 */
export function Can({ perm, mode = 'any', fallback = null, children }: CanProps) {
  return <>{usePermission(perm, mode) ? children : fallback}</>;
}

/** Route guard: shows a 403 state (or `fallback`) when the permission is missing. */
export function RequirePermission({ perm, mode = 'any', fallback, children }: CanProps) {
  const allowed = usePermission(perm, mode);
  if (allowed) return <>{children}</>;
  return <>{fallback ?? <ForbiddenState />}</>;
}

/** Redirects anonymous users to `<shell>/login`, keeping the requested location. */
export function RequireAuth({ children, loginPath = '/login' }: { children: ReactNode; loginPath?: string }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <PageLoader fullScreen />;
  if (status === 'anonymous') return <Navigate to={loginPath} replace state={{ from: location }} />;
  return <>{children}</>;
}

/** Inverse guard for the login pages: authenticated users go to the shell home (or `from`). */
export function RedirectIfAuthenticated({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <PageLoader fullScreen />;
  if (status === 'authenticated') {
    const from = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from;
    const to = from?.pathname && from.pathname !== '/login' ? `${from.pathname}${from.search ?? ''}` : '/';
    return <Navigate to={to} replace />;
  }
  return <>{children}</>;
}
